import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Breadcrumb, Button, Card, Dropdown, Empty, Input, Modal, Space, Table, Tooltip, Typography, message } from 'antd';
import {
    DeleteOutlined, DownloadOutlined, EyeOutlined, FileOutlined, FolderAddOutlined, FolderFilled,
    HomeOutlined, LinkOutlined, ReloadOutlined, UploadOutlined,
} from '@ant-design/icons';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from '../../../shared/i18n';
import { fmtBytes, fmtDate, s3, toastErr } from './common';
import { enqueueUploads, filesFromDrop, subscribe } from './upload';

const { Text, Link: TLink } = Typography;

const PAGE = 200;
const LINK_TTLS = [
    { key: '3600', label: '1 hour' },
    { key: '86400', label: '1 day' },
    { key: '604800', label: '7 days' },
];

const ObjectBrowser = ({ bucket }) => {
    const { t } = useTranslation();
    const id = bucket?.id;
    const [sp, setSp] = useSearchParams();
    const prefix = sp.get('prefix') || '';

    const [prefixes, setPrefixes] = useState([]);
    const [objects, setObjects] = useState([]);
    const [next, setNext] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState([]);
    const [dragging, setDragging] = useState(false);
    const [folderOpen, setFolderOpen] = useState(false);
    const [folderName, setFolderName] = useState('');
    const fileInput = useRef(null);
    const reqSeq = useRef(0);
    const loadRef = useRef(null);

    const setPrefix = (p) => {
        const nsp = new URLSearchParams(sp);
        if (p) nsp.set('prefix', p); else nsp.delete('prefix');
        setSp(nsp);
    };

    const load = useCallback(async (token) => {
        if (!id) return;
        const seq = ++reqSeq.current;
        setLoading(true);
        try {
            const d = await s3.get(`/buckets/${id}/objects`, { prefix, token: token || undefined, limit: PAGE });
            if (seq !== reqSeq.current) return;
            const ps = Array.isArray(d?.prefixes) ? d.prefixes : [];
            const os = Array.isArray(d?.objects) ? d.objects : [];
            if (token) {
                setPrefixes((x) => [...x, ...ps]);
                setObjects((x) => [...x, ...os]);
            } else {
                setPrefixes(ps);
                setObjects(os);
                setSelected([]);
            }
            setNext(d?.next || null);
        } catch (e) {
            if (seq === reqSeq.current) toastErr(e, t('Failed to load'));
        } finally {
            if (seq === reqSeq.current) setLoading(false);
        }
    }, [id, prefix, t]);

    useEffect(() => { loadRef.current = load; load(); }, [load]);

    // uploads run console-wide (UploadPanel); refresh the listing when one into this bucket finishes
    const doneCount = useRef(-1);
    useEffect(() => subscribe((jobs) => {
        const n = jobs.filter((j) => j.bucketId === id && j.status === 'done').length;
        if (doneCount.current >= 0 && n > doneCount.current) loadRef.current?.();
        doneCount.current = n;
    }), [id]);

    const rows = useMemo(() => [
        ...prefixes.filter((p) => p !== prefix).map((p) => ({ rk: `d:${p}`, dir: true, key: p })),
        ...objects.filter((o) => o.key !== prefix).map((o) => ({ rk: `f:${o.key}`, dir: false, ...o })),
    ], [prefixes, objects, prefix]);

    // ---------------- upload ----------------
    const enqueue = (list) => {
        const items = (list || []).filter((x) => x?.file).map(({ file, path }) => ({ file, key: prefix + (path || file.name) }));
        enqueueUploads(bucket, items);
    };

    const onDrop = async (e) => {
        e.preventDefault();
        setDragging(false);
        try {
            enqueue(await filesFromDrop(e.dataTransfer));
        } catch (_) {
            enqueue(Array.from(e.dataTransfer?.files || []).map((f) => ({ file: f, path: f.name })));
        }
    };

    // ---------------- actions ----------------
    const presign = (key, inline, expires) => s3.get(`/buckets/${id}/objects/download`, { key, inline: inline ? 1 : 0, expires });

    const download = async (key) => {
        try {
            const d = await presign(key, false, 3600);
            if (d?.url) window.location.assign(d.url);
        } catch (e) { toastErr(e); }
    };

    const preview = async (key) => {
        const w = window.open('', '_blank');
        try {
            const d = await presign(key, true, 3600);
            if (d?.url && w) w.location.href = d.url; else w?.close();
        } catch (e) {
            w?.close();
            toastErr(e);
        }
    };

    const copyLink = async (key, expires) => {
        let url = '';
        try {
            const d = await presign(key, true, Number(expires));
            url = d?.url || '';
        } catch (e) { toastErr(e); return; }
        if (!url) return;
        try {
            await navigator.clipboard.writeText(url);
            message.success(t('Link copied'));
        } catch (_) {
            Modal.info({ title: t('Share link'), width: 560, content: <Input.TextArea readOnly autoSize value={url} /> });
        }
    };

    const remove = (keys, dirs) => {
        const n = keys.length + dirs.length;
        Modal.confirm({
            title: t('Delete {{count}} item(s)?', { count: n }),
            content: dirs.length ? t('Folders are deleted with everything inside') : undefined,
            okText: t('Delete'),
            okButtonProps: { danger: true },
            onOk: async () => {
                try {
                    const d = await s3.post(`/buckets/${id}/objects/delete`, { keys, prefixes: dirs });
                    message.success(t('Deleted {{count}}', { count: d?.deleted ?? n }));
                    load();
                } catch (e) { toastErr(e); }
            },
        });
    };

    const removeSelected = () => {
        const sel = rows.filter((r) => selected.includes(r.rk));
        remove(sel.filter((r) => !r.dir).map((r) => r.key), sel.filter((r) => r.dir).map((r) => r.key));
    };

    const createFolder = async () => {
        const name = folderName.trim().replace(/^\/+|\/+$/g, '');
        if (!name) return;
        try {
            await s3.post(`/buckets/${id}/folders`, { prefix: `${prefix}${name}/` });
            setFolderOpen(false);
            setFolderName('');
            load();
        } catch (e) { toastErr(e); }
    };

    // ---------------- render ----------------
    const segs = prefix.split('/').filter(Boolean);
    const crumbs = [
        { title: <TLink onClick={() => setPrefix('')}><HomeOutlined /> {bucket?.name}</TLink> },
        ...segs.map((s, i) => {
            const p = `${segs.slice(0, i + 1).join('/')}/`;
            return { title: i === segs.length - 1 ? <Text strong>{s}</Text> : <TLink onClick={() => setPrefix(p)}>{s}</TLink> };
        }),
    ];

    const cols = [
        {
            title: t('Name'), key: 'name', ellipsis: true,
            render: (_, r) => {
                const name = r.key.slice(prefix.length);
                return r.dir
                    ? <TLink onClick={() => setPrefix(r.key)}><FolderFilled style={{ color: '#f0b429', marginRight: 8 }} />{name.replace(/\/$/, '')}</TLink>
                    : <span><FileOutlined style={{ marginRight: 8, opacity: 0.6 }} />{name}</span>;
            },
        },
        { title: t('Size'), dataIndex: 'size', width: 110, render: (v, r) => (r.dir ? '-' : fmtBytes(v)) },
        { title: t('Modified'), dataIndex: 'modified', width: 180, responsive: ['md'], render: (v, r) => (r.dir ? '-' : fmtDate(v)) },
        {
            title: '', key: 'a', width: 150, align: 'right',
            render: (_, r) => (r.dir
                ? <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove([], [r.key])} />
                : (
                    <Space size={0}>
                        <Tooltip title={t('Download')}><Button size="small" type="text" icon={<DownloadOutlined />} onClick={() => download(r.key)} /></Tooltip>
                        <Tooltip title={t('Open')}><Button size="small" type="text" icon={<EyeOutlined />} onClick={() => preview(r.key)} /></Tooltip>
                        <Dropdown trigger={['click']} menu={{
                            items: LINK_TTLS.map((x) => ({ key: x.key, label: t('Copy link · {{ttl}}', { ttl: t(x.label) }) })),
                            onClick: ({ key }) => copyLink(r.key, key),
                        }}>
                            <Tooltip title={t('Copy link')}><Button size="small" type="text" icon={<LinkOutlined />} /></Tooltip>
                        </Dropdown>
                        <Tooltip title={t('Delete')}><Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove([r.key], [])} /></Tooltip>
                    </Space>
                )),
        },
    ];

    return (
        <div
            onDragOver={(e) => { e.preventDefault(); if (!dragging) setDragging(true); }}
            onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
            onDrop={onDrop}
            style={{ position: 'relative' }}
        >
            {dragging && (
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 10, border: '2px dashed #1677ff', borderRadius: 8,
                    background: 'rgba(22,119,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none',
                }}>
                    <Text strong><UploadOutlined /> {t('Drop to upload to {{path}}', { path: `/${prefix}` })}</Text>
                </div>
            )}

            <Card size="small" style={{ marginBottom: 12 }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center', justifyContent: 'space-between' }}>
                    <Breadcrumb items={crumbs} />
                    <Space wrap>
                        {selected.length > 0 && (
                            <Button danger icon={<DeleteOutlined />} onClick={removeSelected}>{t('Delete ({{count}})', { count: selected.length })}</Button>
                        )}
                        <Button icon={<ReloadOutlined />} onClick={() => load()} loading={loading} />
                        <Button icon={<FolderAddOutlined />} onClick={() => setFolderOpen(true)}>{t('New folder')}</Button>
                        <Button type="primary" icon={<UploadOutlined />} onClick={() => fileInput.current?.click()}>{t('Upload')}</Button>
                        <input ref={fileInput} type="file" multiple hidden
                            onChange={(e) => { enqueue(Array.from(e.target.files || []).map((f) => ({ file: f, path: f.name }))); e.target.value = ''; }} />
                    </Space>
                </div>
            </Card>

            <Card size="small" styles={{ body: { padding: 0 } }}>
                <Table rowKey="rk" columns={cols} dataSource={rows} loading={loading} size="small" pagination={false}
                    rowSelection={{ selectedRowKeys: selected, onChange: setSelected }}
                    onRow={(r) => ({ onDoubleClick: () => (r.dir ? setPrefix(r.key) : download(r.key)) })}
                    locale={{
                        emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('Drag files here or click Upload')} />,
                    }} />
                {next && (
                    <div style={{ textAlign: 'center', padding: 12 }}>
                        <Button onClick={() => load(next)} loading={loading}>{t('Load more')}</Button>
                    </div>
                )}
            </Card>

            <Modal open={folderOpen} title={t('New folder')} onCancel={() => setFolderOpen(false)} onOk={createFolder}
                okButtonProps={{ disabled: !folderName.trim() }} destroyOnHidden>
                <Input autoFocus value={folderName} onChange={(e) => setFolderName(e.target.value)} onPressEnter={createFolder}
                    addonBefore={`/${prefix}`} placeholder={t('Folder name')} />
            </Modal>
        </div>
    );
};

export default ObjectBrowser;
